import IllegalOperationError from './IllegalOperationError';
import ow from 'ow';
import LoginViewState from './LoginViewState';
import { isBlank } from './lib';
import { notBlankValidator } from './owValidators';

const LOCK_SIGN_IN_PAGE = 'login';
const LOCK_CREATE_ACCOUNT_PAGE = 'signUp';
const LOCK_FORGOT_PASSWORD_PAGE = 'forgotPassword';

const SIGN_IN_FLASH =
  'We found an existing account for that email address. Please enter your password to continue.';

const SIGN_UP_FLASH =
  "We couldn't find a Ramsey account with that email. Let's create one!";

const MAINTENANCE_MODE_VIEW_STATE = new LoginViewState().withText({
  heading: "We'll be back soon!",
  subheading:
    "We're currently performing maintenance on this site. Please check back later today and we'll be glad to serve you!",
});

function LoginView(
  appName,
  emailFirstClient,
  maintenanceModeEnabled,
  txid,
  logError
) {
  const state = {
    email: '',
    lockInstance: null,
    isEmailReadOnly: false,
    maintenanceModeEnabled: maintenanceModeEnabled,
  };

  const screenText = () => ({
    email: {
      heading: 'Enter Your Email',
      subheading: `to continue to ${appName}`,
    },
    login: {
      heading: 'Enter Your Password',
      subheading: `to continue to ${appName}`,
    },
    signUp: {
      heading: 'Create Your Password',
      subheading: `to continue to ${appName}`,
    },
    forgotPassword: {
      heading: 'Forgot Your Password?',
      subheading: `Click the button below to send an email to ${state.email} to reset your password.`,
    },
    passwordStrength: {
      containsAtLeast:
        'Must contain at least %d of the following %d types of characters:',
      numbers: 'Numbers (i.e. 0–9)',
      lengthAtLeast: 'Must be at least %d characters in length',
      lowerCase: 'Lower case letters (a–z)',
      upperCase: 'Upper case letters (A–Z)',
    },
  });

  const updateEmail = (email) => {
    state.email = email;
  };

  const showExplanationForUnexpectedScreen = (view, flashText) => {
    if (state.requestedScreen === view) {
      return {};
    }

    return {
      flashMessage: {
        type: 'info',
        text: flashText,
      },
    };
  };

  const showEmailForm = (isErrorVisible = false) =>
    new LoginViewState()
      .withEmail(state.email)
      .withText(screenText().email)
      .withIsErrorVisible(isErrorVisible)
      .withIsEmailFormVisible(true);

  const showSignIn = () =>
    new LoginViewState()
      .withEmail(state.email)
      .withText(screenText().login)
      .withIsForgotPasswordButtonVisible(true)
      .withIsBackToEmailButtonVisible(!state.isEmailReadOnly)
      .withIsLoginHintEmailDisplayVisible(state.isEmailReadOnly)
      .withLockState({
        lockOptionOverrides: {
          prefill: { email: state.email },
          initialScreen: LOCK_SIGN_IN_PAGE,
        },
        showOptions: showExplanationForUnexpectedScreen(
          LOCK_SIGN_IN_PAGE,
          SIGN_IN_FLASH
        ),
      });

  const showSignUp = () =>
    new LoginViewState()
      .withEmail(state.email)
      .withText(screenText().signUp)
      .withIsBackToEmailButtonVisible(!state.isEmailReadOnly)
      .withIsLoginHintEmailDisplayVisible(state.isEmailReadOnly)
      .withLockState({
        lockOptionOverrides: {
          prefill: { email: state.email },
          initialScreen: LOCK_CREATE_ACCOUNT_PAGE,
        },
        showOptions: showExplanationForUnexpectedScreen(
          LOCK_CREATE_ACCOUNT_PAGE,
          SIGN_UP_FLASH
        ),
      });

  const showForgotPassword = () =>
    new LoginViewState()
      .withEmail(state.email)
      .withText(screenText().forgotPassword)
      .withIsBackToSignInButtonVisible(true)
      .withLockState({
        lockOptionOverrides: {
          prefill: { email: state.email },
          initialScreen: LOCK_FORGOT_PASSWORD_PAGE,
        },
        showOptions: {},
      });

  const showFederatedSignIn = (email, federatedConnection) =>
    new LoginViewState()
      .withEmail(email)
      .withFederatedConnection(federatedConnection);

  const lookupEmail = async (email) => {
    ow(email, 'email', ow.string.validate(notBlankValidator));
    updateEmail(email);
    try {
      const id = await emailFirstClient.lookup(email, txid);
      if (id.isFederated) {
        return showFederatedSignIn(id.email, id.federatedConnection);
      }
      return id.isKnown ? showSignIn() : showSignUp();
    } catch (e) {
      if (state.isEmailReadOnly) {
        // If login_hint was specified, we don't have a screen to stay on,
        //   so just let it bubble up as a fatal error.
        throw e;
      }
      logError(e);
      return showEmailForm(true);
    }
  };

  const init = async (email, requestedScreen = LOCK_SIGN_IN_PAGE) => {
    state.requestedScreen = requestedScreen;
    if (state.maintenanceModeEnabled) {
      return MAINTENANCE_MODE_VIEW_STATE;
    }

    if (isBlank(email)) {
      return showEmailForm();
    }

    state.isEmailReadOnly = true;
    return await lookupEmail(email);
  };

  const assertWriteable = () => {
    if (state.isEmailReadOnly) {
      throw new IllegalOperationError('Email provided to init() is read-only.');
    }
  };

  const assertEmailPresent = () => {
    if (isBlank(state.email)) {
      throw new IllegalOperationError('No email has been provided.');
    }
  };

  return {
    onEmailFormSubmit: async (email) => {
      assertWriteable();
      return await lookupEmail(email);
    },
    forgotPassword: async () => {
      assertEmailPresent();
      return showForgotPassword();
    },
    backToSignin: async () => {
      assertEmailPresent();
      return showSignIn();
    },
    backToEmail: async () => {
      assertWriteable();
      assertEmailPresent();
      return showEmailForm();
    },
    init,
  };
}

export default LoginView;
